import { ComponentBase } from 'utils/abstract-classes/component-base'
import { attachEventHandlerWithFemFlagCheck } from 'utils/events'
import { LocalStorageKey } from 'constants/enums'
import { logger } from 'utils/system/logger'

declare global {
    interface Window {
        chtmx: {
            handleAllSelectorsVisibility: (
                selector: string,
                action: string
            ) => void
            togglePasswordVisibility: (button: HTMLButtonElement) => void
        }
    }
}

class ApiError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'ApiError'
    }
}
export class MyAccountComponent extends ComponentBase {
    private timeoutId: ReturnType<typeof setTimeout> | undefined = undefined
    private signOutInitiated = false
    public constructor() {
        super()

        // Add sign out event listener
        this.addSignOutEventListener()

        // Add HTMX event listeners for advance client side functionality
        this.addHtmxEventListeners()
        this.getLRToken(
            () => {
                // token is not available in local storage
                // If the download prepared data button is visible, wait till the user clicks on the button
                // and then redirect to sign in, otherwise chrome will not allow the redirects, it closes the tab
                // because this tab was opened from an email link and the user did not interact with the page
                // Should not redirect to sign in page if sign out is initiated
                if (
                    !this.isDownloadPreparedDataButtonVisible() &&
                    !this.signOutInitiated
                ) {
                    //redirect the page for anonymous users
                    this.redirectToSignIn()
                    return
                }
            },
            () => {
                // token is available in local storage
                const loader = document.getElementById(
                    'js-pn-id-ready-page-loader'
                )
                if (loader) {
                    loader.setAttribute('hx-trigger', 'load')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ;(window.htmx as any)?.process(loader)
                }
            }
        )
        this.addMyNewslettersPageEventListeners()

        window.chtmx = window.chtmx || {}
        window.chtmx.handleAllSelectorsVisibility =
            this.handleAllSelectorsVisibility
        window.chtmx.togglePasswordVisibility = this.togglePasswordVisibility

        // Add event listener for request gdpr download button
        this.addRequestGdprDownloadEventListener()

        // Add event listener for download gdpr prepared data button
        this.addDownloadGdprPreparedDataEventListener()

        // Add event listener for gdpr request delete button
        this.addGdprRequestDeleteEventListeners()

        // Start downloading the prepared GDPR data if user clicked on the link from
        // legacy profile service email notification
        this.handleLegacyGDPRDownload()

        // Add event listener for status message dismiss button
        this.addStatusMessageDismissEventListener()
    }

    public redirectToSignIn(): void {
        sessionStorage.setItem('redirect_url', window.location.href)
        window.location.href = '/sign-in'
    }

    public addMyNewslettersPageEventListeners(): void {
        document.addEventListener('htmx:afterRequest', (evt: CustomEvent) => {
            if (evt.detail.target.id === 'newsletters-info__message') {
                // eslint-disable-next-line no-magic-numbers
                if (evt.detail.xhr.status === 200) {
                    // Delete one card if list id exist in paramters
                    if (evt.detail.requestConfig.parameters.list_id) {
                        const list_id =
                            evt.detail.requestConfig.parameters.list_id.toLowerCase()
                        document
                            .getElementById('newsletters-info__card-' + list_id)
                            ?.remove()
                    } else if (evt.detail.requestConfig.parameters.all_lists) {
                        // if paid doesn't exist hide the whole detail element else hide regular card only
                        const paid_card =
                            document.querySelectorAll('[role="paid"]')
                        if (paid_card && paid_card.length > 0) {
                            const regular_subs =
                                document.querySelectorAll('[role="regular"]')
                            regular_subs.forEach((el) => {
                                el?.remove()
                            })
                        } else {
                            document
                                .getElementById('newsletters-info__details')
                                ?.remove()
                        }
                    }
                } else {
                    const message_ele = document.getElementById(
                        evt.detail.target.id
                    )
                    if (message_ele) {
                        message_ele.innerHTML = evt.detail.xhr.response
                    }
                }
            }
        })
    }

    /**
     * Get the user's token from local storage
     * @param callbackWithoutToken Callback function to execute if the token is not available
     * @param callbackWithToken Callback function to execute if the token is available
     * @returns {void}
     */
    public getLRToken(
        callbackWithoutToken: () => void,
        callbackWithToken: (token: string) => void
    ): void {
        attachEventHandlerWithFemFlagCheck('pnIdReady', (evt: CustomEvent) => {
            const token = localStorage.getItem(LocalStorageKey.LRTOKENKEY)
            const userToken =
                token && token !== 'null' && token !== 'undefined'
                    ? token
                    : undefined
            if (userToken) {
                logger('info', 'pnIdReady event handler executed', evt?.detail)
                callbackWithToken(userToken)
            } else {
                callbackWithoutToken()
            }
        })
    }

    /**
     * Add event listener for the request GDPR download button
     */
    public addRequestGdprDownloadEventListener(): void {
        const requestGdprDownloadButton: HTMLButtonElement | null =
            document.querySelector('.gdpr-request-download__button')
        const downloadGdprPreparedDataButton: HTMLButtonElement | null =
            document.querySelector('.gdpr-download-data__button')
        requestGdprDownloadButton?.addEventListener('click', (e) => {
            e.preventDefault()
            this.hideStatusMessage()
            this.getLRToken(
                () => {
                    // token is not available in local storage
                    //redirect the page for anonymous users
                    this.redirectToSignIn()
                    return
                },
                (lrToken) => {
                    // prevent multiple clicks
                    requestGdprDownloadButton.textContent = 'REQUESTING...'
                    requestGdprDownloadButton.disabled = true
                    if (downloadGdprPreparedDataButton) {
                        // hide download button if it's visible
                        downloadGdprPreparedDataButton.style.display = 'none'
                    }
                    const requestUrl =
                        requestGdprDownloadButton.getAttribute(
                            'data-request-url'
                        )
                    if (requestUrl) {
                        this.requestGdprDownload(requestUrl, lrToken)
                            .then((responseText) => {
                                // display the success response in the status message bar
                                this.displayStatusMessage(true, responseText)
                                requestGdprDownloadButton.textContent =
                                    'PERSONAL DATA DOWNLOAD REQUESTED'
                            })
                            .catch((err) => {
                                // display the error message in the status message bar
                                this.displayStatusMessage(false, err.message)
                                requestGdprDownloadButton.textContent =
                                    'REQUEST PERSONAL DATA'
                                requestGdprDownloadButton.disabled = false
                            })
                    }
                }
            )
        })
    }

    /**
     * Request GDPR download
     * @param requestUrl api endpoint to request GDPR download
     * @param lrTokenKey user's token
     * @returns {Promise<string>} - The response text from the server
     * @throws {Error} - If the request fails
     */
    public async requestGdprDownload(
        requestUrl: string,
        lrTokenKey: string
    ): Promise<string> {
        return fetch(requestUrl, {
            method: 'GET',
            headers: { 'X-API-U-TOKEN': lrTokenKey }
        })
            .then(async (response) => {
                if (!response.ok) {
                    // to differentiate between an error message from the api and any other error,
                    // we can check the status code of the response, the api will return a 400 status code
                    const apiErrorStatus = 400
                    if (response.status === apiErrorStatus) {
                        const text = await response.text()
                        throw new ApiError(text)
                    }
                    // any other status means it's not from the api, display a generic error message
                    // to avoid displaying non user-friendly error messages
                    throw new ApiError(
                        'Something went wrong. Please try again!'
                    )
                }
                // the server will return the appropriate success response
                return await response.text()
            })
            .catch((err) => {
                if (err instanceof ApiError) {
                    // the server will return the appropriate error message
                    throw new ApiError(err.message)
                }
                // network error
                throw new Error(
                    'Network error: Please check your internet connection and try again.'
                )
            })
    }

    /**
     * Listens for sign out event (dispatched in FEM sign out function)
     */
    public addSignOutEventListener(): void {
        document.addEventListener('pnIdSignOut', () => {
            this.signOutInitiated = true
        })
    }

    public isDownloadPreparedDataButtonVisible(): boolean {
        const downloadGdprPreparedDataButton: HTMLButtonElement | null =
            document.querySelector('.gdpr-download-data__button')
        return downloadGdprPreparedDataButton ? true : false
    }

    /**
     * Add event listener for the download GDPR prepared data button
     */
    public addDownloadGdprPreparedDataEventListener(): void {
        const downloadGdprPreparedDataButton: HTMLButtonElement | null =
            document.querySelector('.gdpr-download-data__button')
        downloadGdprPreparedDataButton?.addEventListener('click', (e) => {
            e.preventDefault()
            // to avoid confusion with any previous status messages
            this.hideStatusMessage()
            this.getLRToken(
                () => {
                    // token is not available in local storage
                    //redirect the page for anonymous users
                    this.redirectToSignIn()
                    return
                },
                (lrToken) => {
                    // prevent multiple clicks
                    downloadGdprPreparedDataButton.textContent =
                        'DOWNLOADING...'
                    downloadGdprPreparedDataButton.disabled = true
                    const downloadUrl =
                        downloadGdprPreparedDataButton.getAttribute(
                            'data-download-url'
                        )
                    if (downloadUrl) {
                        this.downloadPreparedData(downloadUrl, lrToken)
                            .then(() => {
                                // display the success message in the status message bar
                                this.displayStatusMessage(
                                    true,
                                    'Your data has been downloaded successfully.'
                                )
                            })
                            .catch((err) => {
                                // display the error message in the status message bar
                                this.displayStatusMessage(false, err.message)
                            })
                            .finally(() => {
                                downloadGdprPreparedDataButton.textContent =
                                    'DOWNLOAD THE PROCESSED PERSONAL DATA'
                                downloadGdprPreparedDataButton.disabled = false
                            })
                    }
                }
            )
        })
    }

    /**
     * Download prepared GDPR data
     * @param downloadUrl api endpoint to download prepared data
     * @param lrTokenKey user's token
     * @returns {Promise<void>} - The response from the server
     * @throws {Error} - If the request fails
     */
    public async downloadPreparedData(
        downloadUrl: string,
        lrTokenKey: string
    ): Promise<void> {
        return fetch(downloadUrl, {
            method: 'GET',
            headers: { 'X-API-U-TOKEN': lrTokenKey }
        })
            .then(async (response) => {
                if (!response.ok) {
                    // the server will return the appropriate error message (link expired, invalid link, etc)
                    // with a status code of 400, any other status code means it's not from the api
                    const apiErrorStatus = 400
                    if (response.status == apiErrorStatus) {
                        const text = await response.text()
                        throw new ApiError(text)
                    }
                    throw new ApiError(
                        'Something went wrong. Please try again!'
                    )
                }
                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const hiddenAnchor = document.createElement('a')
                if ('download' in hiddenAnchor) {
                    hiddenAnchor.href = url
                    hiddenAnchor.download = 'your_data.zip'
                    document.body.appendChild(hiddenAnchor)
                    hiddenAnchor.click()
                    document.body.removeChild(hiddenAnchor)
                } else {
                    window.open(url)
                }
            })
            .catch((err) => {
                if (err instanceof ApiError) {
                    // the server will return the appropriate error message
                    throw new ApiError(err.message)
                }
                // network error
                throw new Error(
                    'Network error: Please check your internet connection and try again.'
                )
            })
    }

    /**
     * Add event listeners for the GDPR request delete
     * button and its modal elements
     * @returns {void}
     */
    public addGdprRequestDeleteEventListeners(): void {
        // gdpr delete modal is hidden by default
        const modal: HTMLDivElement | null = document.querySelector(
            '.request-delete-modal'
        )
        this.handleModalEvents(modal)
        // main button on page to request delete
        const requestGdprDeleteButton: HTMLButtonElement | null =
            document.querySelector('.gdpr-request-delete__button')
        requestGdprDeleteButton?.addEventListener('click', (e) => {
            e.preventDefault()
            this.displayModal(modal)
        })
        // cancel button is inside the modal
        const cancelButton: HTMLButtonElement | null = document.querySelector(
            '.settings-delete-popup__cancel-button'
        )
        cancelButton?.addEventListener('click', () => {
            if (modal) {
                modal.style.display = 'none'
            }
        })
        // confirm button is inside the modal
        const confirmButton: HTMLButtonElement | null = document.querySelector(
            '.settings-delete-popup__delete-button'
        )
        confirmButton?.addEventListener('click', (e) => {
            e.preventDefault()
            // to avoid confusion with any previous status messages
            this.hideStatusMessage()
            this.getLRToken(
                () => {
                    // token is not available in local storage
                    //redirect the page for anonymous users
                    this.redirectToSignIn()
                    return
                },
                (lrToken) => {
                    // prevent multiple clicks
                    confirmButton.textContent = 'DELETING...'
                    confirmButton.disabled = true
                    const deleteRequestUrl =
                        confirmButton.getAttribute('data-delete-url')
                    // response div is where the success html content will be inserted
                    const responseDiv: HTMLDivElement | null =
                        document.querySelector('.gdpr-request-delete__response')
                    if (deleteRequestUrl) {
                        this.requestGdprDelete(deleteRequestUrl, lrToken)
                            .then((response) => {
                                // display the success response html from the server into the response div
                                if (responseDiv) {
                                    responseDiv.innerHTML = response
                                    // select the modal html returned from the server containing the confirmation message
                                    const confirmationModal: HTMLDivElement | null =
                                        document.querySelector(
                                            '.confirm-delete-modal'
                                        )
                                    this.handleModalEvents(confirmationModal)
                                    this.displayModal(confirmationModal)
                                }
                            })
                            .catch((err) => {
                                // display the error message in the status message bar
                                this.displayStatusMessage(false, err.message)
                            })
                            .finally(() => {
                                // hide the initial modal
                                if (modal) {
                                    modal.style.display = 'none'
                                }
                                confirmButton.textContent = 'DELETE'
                                confirmButton.disabled = false
                            })
                    }
                }
            )
        })
    }

    /**
     * Request GDPR delete
     * @param requestDeleteUrl api endpoint to request GDPR delete
     * @param lrTokenKey user's token
     * @returns {Promise<string>} - The response text from the server
     * @throws {Error} - If the request fails
     */
    public async requestGdprDelete(
        requestDeleteUrl: string,
        lrTokenKey: string
    ): Promise<string> {
        return fetch(requestDeleteUrl, {
            method: 'DELETE',
            headers: { 'X-API-U-TOKEN': lrTokenKey }
        })
            .then(async (response) => {
                if (!response.ok) {
                    // the server will return the appropriate error message
                    // with a status code of 400, any other status code means it's not from the api
                    const apiErrorStatus = 400
                    if (response.status === apiErrorStatus) {
                        const text = await response.text()
                        throw new ApiError(text)
                    }
                    throw new ApiError(
                        'Something went wrong. Please try again!'
                    )
                }
                // the server will return the appropriate success response html
                return await response.text()
            })
            .catch((err) => {
                if (err instanceof ApiError) {
                    // the server will return the appropriate error message
                    throw new ApiError(err.message)
                }
                // network error
                throw new Error(
                    'Network error: Please check your internet connection and try again.'
                )
            })
    }

    /**
     * Display modal and focus on the first focusable element of the modal
     * @param modal The modal element to display
     * @returns {void}
     */
    public displayModal(modal: HTMLDivElement | null): void {
        if (modal) {
            modal.style.display = 'block'
            const focusElement: HTMLElement | null =
                modal.querySelector('.first-focus')
            focusElement?.focus()
        }
    }

    /**
     * Handle modal events: the close button in the top right corner,
     * the escape key press, clicking outside the modal, and tab key press
     * @param modal The modal element to handle events for
     * @returns {void}
     */
    public handleModalEvents(modal: HTMLDivElement | null): void {
        // close button in the top right corner
        const closeModalButton = modal?.querySelector('.close-modal-button')
        closeModalButton?.addEventListener('click', () => {
            if (modal) {
                modal.style.display = 'none'
            }
        })
        // limit the tabbing to the modal elements only
        const focusableElements = modal?.querySelectorAll('button, [href]')
        const firstFocusableElement = focusableElements?.[0] as HTMLElement
        const lastFocusableElement = focusableElements?.[
            focusableElements.length - 1
        ] as HTMLElement
        // the gdpr delete confirmation modal is blocking, to make sure user signs out after deletion
        const isBlocking = modal?.getAttribute('data-blocking') // if modal is blocking, it will not close on escape key press
        modal?.addEventListener('keydown', (event) => {
            if (
                event.key === 'Escape' &&
                modal.style.display === 'block' &&
                !isBlocking
            ) {
                modal.style.display = 'none'
            }
            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    if (document.activeElement === firstFocusableElement) {
                        lastFocusableElement?.focus()
                        event.preventDefault()
                    }
                } else {
                    if (document.activeElement === lastFocusableElement) {
                        firstFocusableElement?.focus()
                        event.preventDefault()
                    }
                }
            }
        })
        // when user clicks on the external area of the modal, covering the whole screen
        modal?.addEventListener('click', (event) => {
            if (event.target == modal && !isBlocking) {
                modal.style.display = 'none'
            }
        })
    }

    /**
     * Display status message in the status message bar
     * @param isSuccess Whether the message is a success message or an error message
     * @param message The message to display
     * @returns {void}
     */
    public displayStatusMessage(isSuccess: boolean, message: string): void {
        // There are 2 separate divs for success and error messages, only one is displayed at a time
        const successMessageDiv: HTMLDivElement | null = document.querySelector(
            '.account-info__success-result'
        )
        const errorMessageDiv: HTMLDivElement | null = document.querySelector(
            '.account-info__fail-result'
        )
        const targetDiv = isSuccess ? successMessageDiv : errorMessageDiv
        const otherDiv = isSuccess ? errorMessageDiv : successMessageDiv
        if (targetDiv && otherDiv) {
            // The p element is where the message is displayed
            const paragraphElement = targetDiv.querySelector('p')
            // The button element is the dismiss button
            const buttonElement = targetDiv.querySelector('button')
            if (paragraphElement) {
                paragraphElement.textContent = message
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId)
            }
            const messageDuration = 10000
            this.timeoutId = setTimeout(() => {
                targetDiv.style.display = 'none'
                this.timeoutId = undefined
            }, messageDuration)
            targetDiv.style.display = 'flex'
            otherDiv.style.display = 'none'
            if (buttonElement) {
                buttonElement.focus()
            }
        }
    }

    /**
     * Hide the status message in the status message bar
     * @returns {void}
     */
    public hideStatusMessage(): void {
        // hide both success and error messages
        const successMessageDiv: HTMLDivElement | null = document.querySelector(
            '.account-info__success-result'
        )
        const errorMessageDiv: HTMLDivElement | null = document.querySelector(
            '.account-info__fail-result'
        )
        if (successMessageDiv && errorMessageDiv) {
            successMessageDiv.style.display = 'none'
            errorMessageDiv.style.display = 'none'
        }
    }

    /**
     * Add event listener for the status message dismiss button
     * @returns {void}
     */
    public addStatusMessageDismissEventListener(): void {
        // the status message div contains both success and error messages
        const statusMessageDiv: HTMLDivElement | null = document.querySelector(
            '.account-info__message'
        )
        if (statusMessageDiv) {
            // there are 2 buttons, one for success and one for error messages
            const dismissButtons: NodeListOf<HTMLButtonElement> =
                statusMessageDiv.querySelectorAll(
                    '.account-info__dismiss-button'
                )
            dismissButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    this.hideStatusMessage()
                })
            })
        }
    }

    /**
     * Start downloading the prepared GDPR data if user clicked on the link from
     * legacy email notification (from profile service)
     * @returns {void}
     */
    public handleLegacyGDPRDownload(): void {
        // The link in legacy email points to /my-account/ which is the profile service react app
        // The react app saves the encrypted download link parameter in session storage,
        // then redirects to the new my-account page in dcs, where this component is loaded
        const legacyGDPRDownloadLink = sessionStorage.getItem('gdprResultsUrl')
        if (legacyGDPRDownloadLink) {
            this.hideStatusMessage()
            this.getLRToken(
                () => {
                    // token is not available in local storage
                    // redirect to sign in is handled in the constructor
                    return
                },
                (lrToken) => {
                    const downloadUrl = `/my-account/your-data/?u=${legacyGDPRDownloadLink}`
                    // Let the user know that the download is in progress
                    // The download starts immediately after the page is loaded, without the user clicking on a button
                    this.displayStatusMessage(true, 'Downloading your data...')
                    this.downloadPreparedData(downloadUrl, lrToken)
                        .then(() => {
                            // otherwise the download will start every time user switches between tabs in my-account
                            sessionStorage.removeItem('gdprResultsUrl')
                            // display the success message in the status message bar
                            this.displayStatusMessage(
                                true,
                                'Your data has been downloaded successfully.'
                            )
                        })
                        .catch((err) => {
                            // display the error message in the status message bar
                            this.displayStatusMessage(false, err.message)
                        })
                }
            )
        }
    }

    public addHtmxEventListeners(): void {
        document.body.addEventListener('htmx:afterSwap', (evt: CustomEvent) => {
            if (evt.detail.target.id === 'account-info__password-form') {
                this.htmxInitPasswordValidation()

                const passwordChangeForm = evt.detail.target?.querySelector(
                    '.account-info__password-change-form'
                )

                if (!passwordChangeForm) {
                    this.handleAllSelectorsVisibility(
                        '.account-info__cancel-edit-password-form',
                        'hide'
                    )
                    this.handleAllSelectorsVisibility(
                        '.account-info__show-edit-password-form--group',
                        'show'
                    )
                }
            }
        })

        // Set X-API-U-TOKEN header before send htmx request
        // if LR token is not available in local storage, let server side validation handle this
        document.body.addEventListener(
            'htmx:configRequest',
            (evt: CustomEvent) => {
                // get token from local storage
                const token = localStorage.getItem(LocalStorageKey.LRTOKENKEY)
                const lrTokenKey =
                    token && token !== 'null' && token !== 'undefined'
                        ? token
                        : undefined
                if (lrTokenKey) {
                    // set token in header
                    evt.detail.headers['X-API-U-TOKEN'] = lrTokenKey
                } else {
                    // Do nothing at client side, let server side validation handle this
                    logger('warn', 'User Access Token is not available')
                }
            }
        )
    }

    public handleAllSelectorsVisibility(
        selector: string,
        action: string
    ): void {
        document.querySelectorAll(selector).forEach((element) => {
            switch (action) {
                case 'hide':
                    element.classList.add('hidden')
                    break
                case 'show':
                    element.classList.remove('hidden')
                    break
                case 'toggle':
                    element.classList.toggle('hidden')
                    break
                default:
            }
        })
    }

    public togglePasswordVisibility(button: HTMLButtonElement) {
        const parentElement = button.parentElement
        parentElement?.classList.toggle('show-password--visible')
        const buttonText = button.querySelector('.button__text')
        const passwordInput = parentElement?.previousElementSibling
        if (buttonText?.textContent === 'Show') {
            buttonText.textContent = 'Hide'
            if (passwordInput) (passwordInput as HTMLInputElement).type = 'text'
        } else if (buttonText?.textContent === 'Hide') {
            buttonText.textContent = 'Show'
            if (passwordInput)
                (passwordInput as HTMLInputElement).type = 'password'
        }
    }

    public htmxInitPasswordValidation(): void {
        const MIN_PASSWORD_LENGTH = 10
        const submitButton = document.querySelector(
            '.account-info__password-change-submit-button'
        )
        submitButton?.classList.add('disabled')
        submitButton?.setAttribute('disabled', 'true')

        const newPasswordInput = document.getElementById('id_new_password')
        const charCountRequirement = document.getElementById('char-count')
        const numberRequirement = document.getElementById('number')
        const upperLowerRequirement = document.getElementById('upper-lower')
        const specialCharRequirement = document.getElementById('special-char')

        newPasswordInput?.addEventListener('input', () => {
            const password = (newPasswordInput as HTMLInputElement)?.value

            // Check character count
            if (password.length >= MIN_PASSWORD_LENGTH) {
                charCountRequirement?.classList.add(
                    'account-info__password-requirements--valid'
                )
                charCountRequirement?.classList.remove(
                    'account-info__password-requirements--invalid'
                )
            } else {
                charCountRequirement?.classList.add(
                    'account-info__password-requirements--invalid'
                )
                charCountRequirement?.classList.remove(
                    'account-info__password-requirements--valid'
                )
            }

            // Check for at least one number
            if (/\d/.test(password)) {
                numberRequirement?.classList.add(
                    'account-info__password-requirements--valid'
                )
                numberRequirement?.classList.remove(
                    'account-info__password-requirements--invalid'
                )
            } else {
                numberRequirement?.classList.add(
                    'account-info__password-requirements--invalid'
                )
                numberRequirement?.classList.remove(
                    'account-info__password-requirements--valid'
                )
            }

            // Check for uppercase and lowercase letters
            if (/[A-Z]/.test(password) && /[a-z]/.test(password)) {
                upperLowerRequirement?.classList.add(
                    'account-info__password-requirements--valid'
                )
                upperLowerRequirement?.classList.remove(
                    'account-info__password-requirements--invalid'
                )
            } else {
                upperLowerRequirement?.classList.add(
                    'account-info__password-requirements--invalid'
                )
                upperLowerRequirement?.classList.remove(
                    'account-info__password-requirements--valid'
                )
            }

            // Check for special characters
            if (/[\W_]/.test(password)) {
                specialCharRequirement?.classList.add(
                    'account-info__password-requirements--valid'
                )
                specialCharRequirement?.classList.remove(
                    'account-info__password-requirements--invalid'
                )
            } else {
                specialCharRequirement?.classList.add(
                    'account-info__password-requirements--invalid'
                )
                specialCharRequirement?.classList.remove(
                    'account-info__password-requirements--valid'
                )
            }

            if (
                charCountRequirement?.classList.contains(
                    'account-info__password-requirements--invalid'
                ) ||
                numberRequirement?.classList.contains(
                    'account-info__password-requirements--invalid'
                ) ||
                upperLowerRequirement?.classList.contains(
                    'account-info__password-requirements--invalid'
                ) ||
                specialCharRequirement?.classList.contains(
                    'account-info__password-requirements--invalid'
                )
            ) {
                submitButton?.classList.add('disabled')
                submitButton?.setAttribute('disabled', 'true')
            } else {
                submitButton?.classList.remove('disabled')
                submitButton?.removeAttribute('disabled')
            }
        })
    }
}
